import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparePart, CompatibleModel } from '../types/SpareParts';
import { getCompatibleModels } from '../services/sparePartsService';
import { useUser } from '../contexts/UserContext';
import { FileService } from '../services/fileService';
import SparePartImageModal from './SparePartImageModal';

interface SparePartCardProps {
  sparePart: SparePart;
  onAddToCart: (sparePart: SparePart, quantity: number) => void;
}

// Global cache for loaded images
const loadedImagesCache = new Set<string>();

// Custom hook for lazy loading
const useLazyLoading = (src: string | undefined) => {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView && src && !isLoaded && !hasError) {
      const img = new Image();
      img.onload = () => setIsLoaded(true);
      img.onerror = () => setHasError(true);
      img.src = src;
    }
  }, [isInView, src, isLoaded, hasError]);

  return { imgRef, shouldLoad: isInView, isLoaded, hasError };
};

const SparePartCard: React.FC<SparePartCardProps> = ({
  sparePart,
  onAddToCart
}) => {
  const [quantity, setQuantity] = useState(1);
  const [imageError, setImageError] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isLoadingFullImage, setIsLoadingFullImage] = useState(false);
  const [fullImageLoaded, setFullImageLoaded] = useState(false);
  const [preloadedImageDimensions, setPreloadedImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [showCompatibleModels, setShowCompatibleModels] = useState(false);
  const [compatibleModels, setCompatibleModels] = useState<CompatibleModel[]>([]);
  const [loadingCompatibleModels, setLoadingCompatibleModels] = useState(false);
  const { userData } = useUser();

  // Görsel URL'sini oluştur - memoize et
  const imageUrl = useMemo(() => {
    if (!sparePart.Gorsel) return undefined;
    return FileService.getThumbnailUrl(sparePart.Gorsel);
  }, [sparePart.Gorsel]);
  
  // Lazy loading hook'unu kullan
  const { imgRef, shouldLoad, isLoaded, hasError } = useLazyLoading(imageUrl);



  // Görsel modal'ını aç - memoize et
  const openImageModal = useCallback(() => {
    if (sparePart.Gorsel && !imageError) {
      const fullImageUrl = FileService.getDownloadUrl(sparePart.Gorsel);
      
      if (fullImageUrl) {
        // Eğer görsel daha önce yüklenmişse ve boyutlar biliniyorsa direkt modal'ı aç
        if (loadedImagesCache.has(fullImageUrl) && preloadedImageDimensions) {
          setShowImageModal(true);
          return;
        }
        
        // Görseli yükle ve boyutlarını öğren
        setIsLoadingFullImage(true);
        const img = new Image();
        img.onload = () => {
          // Görseli cache'e ekle ve boyutları kaydet
          loadedImagesCache.add(fullImageUrl);
          setPreloadedImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
          
          // Modal'ı aç (artık boyutlar belli)
          setFullImageLoaded(true);
          setIsLoadingFullImage(false);
          setShowImageModal(true);
        };
        img.onerror = () => {
          setIsLoadingFullImage(false);
        };
        img.src = fullImageUrl;
      }
    }
  }, [sparePart.Gorsel, imageError, preloadedImageDimensions]);

  // Görsel modal'ını kapat
  const closeImageModal = () => {
    // Modal'ı anında kapat
    setShowImageModal(false);
    // Diğer state'leri cleanup için ama modal kapanmasını geciktirmesin
    setTimeout(() => {
      setIsLoadingFullImage(false);
      setFullImageLoaded(false);
    }, 0);
  };





  // Miktar artırma
  const incrementQuantity = () => {
    if (quantity < sparePart.Stok) {
      setQuantity(prev => prev + 1);
    }
  };

  // Miktar azaltma
  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  // Miktar değişikliği
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= sparePart.Stok) {
      setQuantity(value);
    }
  };

  // Sepete ekle
  const handleAddToCart = () => {
    onAddToCart(sparePart, quantity);
  };

  // Uyumlu modelleri yükle - memoize et
  const loadCompatibleModels = useCallback(async () => {
    // Eğer daha önce yüklenmiş veriler varsa (başarılı veya başarısız) sadece paneli aç/kapat
    if (compatibleModels.length > 0 || (compatibleModels.length === 0 && showCompatibleModels)) {
      setShowCompatibleModels(!showCompatibleModels);
      return;
    }

    setLoadingCompatibleModels(true);
    try {
      const response = await getCompatibleModels(sparePart.Id);
      setCompatibleModels(response.data);
      setShowCompatibleModels(true);
    } catch (error) {
      console.error('Uyumlu modeller yüklenirken hata:', error);
      // Hata durumunda bile paneli aç (boş liste ile)
      setShowCompatibleModels(true);
    } finally {
      setLoadingCompatibleModels(false);
    }
  }, [compatibleModels.length, showCompatibleModels, sparePart.Id]);

  // Stok durumunu kullanıcı ayarlarına göre göster - memoize et
  const renderStockInfo = useCallback(() => {
    // ShowStock false ise sadece var/yok göster
    if (userData && !userData.ShowStock) {
      return (
        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${sparePart.Stok > 0
            ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 text-green-800 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 dark:text-green-300'
            : 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 text-red-800 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 dark:text-red-300'
        }`}>
          {sparePart.Stok > 0 ? 'Var' : 'Yok'}
        </span>
      );
    }
    
    // ShowStock true ise stok miktarını göster
    return (
      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${sparePart.Stok > 0
          ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 text-green-800 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 dark:text-green-300'
          : 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 text-red-800 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 dark:text-red-300'
      }`}>
        {sparePart.Stok > 0 ? `Stok: ${sparePart.Stok}` : 'Stokta Yok'}
      </span>
    );
  }, [userData?.ShowStock, sparePart.Stok]);

  return (
    <>
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-sm transition-all duration-200 hover:shadow-md flex flex-col h-full">
        <div className="rounded-lg p-4 border border-white dark:border-gray-700 flex flex-col h-full">
        {/* Ürün Görseli */}
        <div className="h-32 mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden p-2 relative">
          {sparePart.Gorsel && !imageError && !hasError ? (
            <>
              <div ref={imgRef} className="w-full h-full">
                {shouldLoad && isLoaded && imageUrl && (
                  <img
                    src={imageUrl}
                    alt={sparePart.Ad}
                    className={`w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity ${isLoadingFullImage ? 'opacity-50' : ''
                    }`}
                    onError={() => setImageError(true)}
                    onClick={openImageModal}
                  />
                )}
                
                {/* Lazy loading placeholder */}
                {(!shouldLoad || !isLoaded) && (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="flex flex-col items-center space-y-1">
                      <svg className="animate-pulse w-8 h-8 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {shouldLoad && !isLoaded && !imageError && !hasError && (
                        <span className="text-xs text-gray-400 dark:text-gray-600">Yükleniyor...</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Loader Overlay */}
              {isLoadingFullImage && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                  <div className="flex flex-col items-center space-y-2">
                    <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-white text-xs font-medium">Yükleniyor...</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

      {/* Ürün Bilgileri */}
      <div className="flex-1 flex flex-col">
        {/* Üst Bilgiler */}
        <div className="space-y-2 flex-1">
        {/* Ürün Kodu */}
        <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">
          {sparePart.Kod}
        </p>

        {/* Ürün Adı */}
        <h3 className="font-medium text-gray-900 dark:text-white text-sm leading-tight truncate">
          {sparePart.Ad}
        </h3>

        {/* Stok Durumu */}
        <div className="flex items-center justify-between">
          {renderStockInfo()}
          {sparePart.PromosyonluMu && (
            <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-br from-orange-100 via-amber-100 to-orange-200 text-orange-800 dark:from-orange-900/30 dark:via-orange-900/25 dark:to-orange-800/30 dark:text-orange-300">
              Promosyon
            </span>
          )}
        </div>

        {/* Fiyat */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Bayi Fiyatı:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
                ₺{sparePart.BayiFiyati.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          
          {sparePart.IscilikBedeli > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">İşçilik:</span>
              <span className="text-sm text-gray-900 dark:text-white">
                  ₺{sparePart.IscilikBedeli.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>

        {/* Uyumlu Modeller */}
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 overflow-hidden">
          <div className="rounded-lg border border-white dark:border-gray-700 overflow-hidden">
          <button
            onClick={loadCompatibleModels}
            disabled={loadingCompatibleModels}
            className="w-full text-left px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-between"
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Uyumlu Modeller
            </span>
            
            {loadingCompatibleModels ? (
              <motion.svg 
                className="h-4 w-4 text-gray-500" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </motion.svg>
            ) : (
              <motion.svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                animate={{ rotate: showCompatibleModels ? 180 : 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </motion.svg>
            )}
          </button>
          
          {/* Uyumlu Modeller Listesi */}
          <AnimatePresence>
            {showCompatibleModels && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ 
                  duration: 0.3, 
                  ease: "easeInOut",
                  opacity: { duration: 0.2 }
                }}
                className="overflow-hidden"
              >
                <div className="px-3 pb-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                  {compatibleModels.length > 0 ? (
                    <motion.div 
                      className="flex flex-wrap gap-1 pt-3"
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1, duration: 0.3 }}
                    >
                      {compatibleModels.map((model, index) => (
                        <motion.span
                          key={model.Id}
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ 
                            delay: 0.2 + (index * 0.05),
                            duration: 0.3,
                            type: "spring",
                            stiffness: 300,
                            damping: 20
                          }}
                          className="inline-block px-2 py-1 bg-gradient-to-br from-blue-100 via-sky-100 to-blue-200 dark:from-blue-900/30 dark:via-blue-900/25 dark:to-blue-800/30 text-blue-800 dark:text-blue-300 text-xs rounded-full font-medium shadow-sm truncate"
                        >
                          {model.Ad}
                        </motion.span>
                      ))}
                    </motion.div>
                  ) : (
                    <motion.p 
                      className="text-xs text-gray-500 dark:text-gray-400 text-center py-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.3 }}
                    >
                      Uyumlu model bilgisi bulunamadı
                    </motion.p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          </div>
        </div>
        </div>

        {/* Miktar ve Sepete Ekle - Alta Sabitlendi */}
        <div className="space-y-3 pt-2 mt-auto">
          {/* Miktar Seçici - Tek ürün varsa gizle, iki ürün varsa boşluk koru */}
            <div className={`flex items-center justify-center space-x-3 ${sparePart.Stok > 0 ? 'visible' : 'hidden sm:flex sm:opacity-0'
          }`}>
            <button
              onClick={decrementQuantity}
              disabled={quantity <= 1 || sparePart.Stok === 0}
              className="border border-gray-300 dark:border-gray-600 rounded-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <div className="w-8 h-8 rounded-full border border-white dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                </svg>
              </div>
            </button>
            
            <input
              type="number"
              min="1"
              max={sparePart.Stok}
              value={quantity}
              onChange={handleQuantityChange}
              disabled={sparePart.Stok === 0}
                className="w-16 text-center border border-gray-300 dark:border-gray-600 rounded-md py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            />
            
            <button
              onClick={incrementQuantity}
              disabled={quantity >= sparePart.Stok || sparePart.Stok === 0}
              className="border border-gray-300 dark:border-gray-600 rounded-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <div className="w-8 h-8 rounded-full border border-white dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
            </button>
          </div>

          {/* Sepete Ekle Butonu */}
          <button
            onClick={sparePart.Stok > 0 ? handleAddToCart : undefined}
            disabled={sparePart.Stok === 0}
              className={`w-full font-semibold py-2.5 px-4 rounded-lg transition-all duration-200 text-sm ${sparePart.Stok > 0
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transform hover:scale-[1.02] active:scale-[0.98] text-white relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            {sparePart.Stok > 0 ? 'Sepete Ekle' : 'Stokta Yok'}
          </button>
        </div>
        </div>
      </div>
    </div>

    {/* Görsel Modal */}
    <SparePartImageModal
      isOpen={showImageModal}
      onClose={closeImageModal}
      sparePart={sparePart}
      isLoadingFullImage={isLoadingFullImage}
      fullImageLoaded={fullImageLoaded}
      preloadedImageDimensions={preloadedImageDimensions || undefined}
    />
  </>
  );
};

export default SparePartCard; 